import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, EnvelopeSimple, SpinnerGap } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import api from '../services/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.verifyEmail(token);
      setStatus('success');
      setMessage(response.data.message);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E1B4B] via-[#312E81] to-[#3730A3] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-white font-['Outfit']">
              Meno<span className="text-[#EC4899]">Path</span>
            </h1>
          </Link>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center">
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 bg-[#5B21B6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <SpinnerGap className="w-8 h-8 text-[#5B21B6] animate-spin" weight="bold" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verifying Your Email</h2>
              <p className="text-white/70 text-sm">Please wait while we verify your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#10B981]" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Email Verified!</h2>
              <p className="text-white/70 text-sm mb-6">{message}</p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] hover:opacity-90 rounded-xl h-11"
              >
                Continue to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-[#DC2626]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-[#DC2626]" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
              <p className="text-white/70 text-sm mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/login">
                  <Button className="w-full bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] hover:opacity-90 rounded-xl h-11">
                    Go to Login
                  </Button>
                </Link>
                <p className="text-white/50 text-xs">
                  Need a new verification link? Log in and request a new one from your dashboard.
                </p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
